import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useRef, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Body } from "@hypercard/web-components";
import { FilterCategoryEnum, FilterOption } from "@common/utils/graphql";
import { useQueryFilterOptions } from "./hooks";
import { CategoryItem } from "./CatergoryItem";
import { CategoryHeading } from "./CategoryHeading";
import { OptionItem } from "./OptionItem";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/utils";
import { CustomFieldItem } from "./CustomFieldItem";

interface Props {
  value: FilterOption[];
  onSelect: (option: FilterOption) => void;
  categories: FilterCategoryEnum[];
  className?: string;
}

// focus input after submenu is entered or exited
export const SelectFilterOptions = ({
  value,
  onSelect,
  categories,
  className,
}: Props) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState("");
  const [categoryOrCustomField, setCategoryOrCustomField] = useState<
    FilterCategoryEnum | string | null
  >(null);
  const [open, setOpen] = useState(false);
  const { optionsByCategory, optionsByCustomField } = useQueryFilterOptions(
    searchText,
    categories,
  );

  const handleSelectCategoryOrCustomField = (
    cat: FilterCategoryEnum | string | null,
  ) => {
    searchRef.current?.focus();
    setCategoryOrCustomField(cat);
  };

  return (
    <div className="flex flex-col space-y-2">
      <Label>Filter By</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="button"
            aria-expanded={open}
            className={cn(
              "justify-start space-x-2 border-none bg-[#1c1c1c] px-3 text-white",
              className,
            )}
          >
            <MagnifyingGlass className="size-5 shrink-0 opacity-50" />
            <Body size="sm" weight="medium" className="text-white/70">
              Search
            </Body>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={-40}
          className="w-[var(--radix-popper-anchor-width)] p-0 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
        >
          <Command shouldFilter={false}>
            <CommandInput
              ref={searchRef}
              onValueChange={setSearchText}
              value={searchText}
              placeholder="Search Filters"
            />
            <CommandList>
              {categoryOrCustomField ? (
                <CommandGroup
                  heading={
                    <CategoryHeading
                      categoryOrCustomFieldName={
                        categoryOrCustomField as FilterCategoryEnum | string
                      }
                      onClick={() => handleSelectCategoryOrCustomField(null)}
                    />
                  }
                  className="cursor-pointer hover:text-white"
                >
                  {optionsByCategory[
                    categoryOrCustomField as FilterCategoryEnum
                  ] &&
                    optionsByCategory[
                      categoryOrCustomField as FilterCategoryEnum
                    ].map((option) => (
                      <OptionItem
                        key={`${option.category}-${option.id}`}
                        option={option}
                        className="pl-4"
                        onSelect={onSelect}
                        value={value}
                      />
                    ))}
                  {optionsByCustomField[categoryOrCustomField as string] &&
                    optionsByCustomField[categoryOrCustomField as string].map(
                      (option) => (
                        <OptionItem
                          key={`${option.displaySubtitle}-${option.id}`}
                          option={option}
                          className="pl-4"
                          onSelect={onSelect}
                          value={value}
                        />
                      ),
                    )}
                </CommandGroup>
              ) : searchText.length > 0 ? (
                <CommandGroup>
                  {Object.keys(optionsByCategory).map((cat) =>
                    optionsByCategory[cat as FilterCategoryEnum].map(
                      (option) => (
                        <OptionItem
                          key={`${option.category}-${option.id}`}
                          option={option}
                          onSelect={onSelect}
                          value={value}
                        />
                      ),
                    ),
                  )}
                  {Object.keys(optionsByCustomField).map((cat) =>
                    optionsByCustomField[cat].map((option) => (
                      <OptionItem
                        key={`${option.displaySubtitle}-${option.id}`}
                        option={option}
                        onSelect={onSelect}
                        value={value}
                      />
                    )),
                  )}
                </CommandGroup>
              ) : (
                <CommandGroup>
                  {Object.keys(optionsByCategory).map((category) => (
                    <CategoryItem
                      key={category}
                      onSelect={handleSelectCategoryOrCustomField}
                      category={category as FilterCategoryEnum}
                    />
                  ))}
                  {Object.keys(optionsByCustomField).map((customFieldName) => (
                    <CustomFieldItem
                      key={customFieldName}
                      onSelect={handleSelectCategoryOrCustomField}
                      customFieldName={customFieldName}
                    />
                  ))}
                </CommandGroup>
              )}
              <CommandEmpty>
                <Body size="md" weight="regular" className="text-white/70">
                  No matching filters
                </Body>
              </CommandEmpty>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
